import { FC } from "react"

import { ITranslations } from "@/types"
import { Grid, GridItem } from "components/Grid"
import FormField from "components/FormField/FormField"
import Input from "components/Input/Input"
import ValidationMessage from "components/ValidationMessage/ValidationMessage"
import { ITranslationsValidation } from "./hooks/useTranslationsValidation"
import Language from "models/Language"
import NoValue from "components/NoValue/NoValue"
import { INPUT_MAX_LENGTH_DEFAULT, INPUT_MAX_LENGTH_LARGE } from "config/constants"
import Text from "components/Text/Text"

export interface ITranslationsProps {
    translations: ITranslations
    onChange(translations: ITranslations): void
    validationErrors?: ITranslationsValidation
    direction?: "horizontal" | "vertical"
    readOnly?: boolean
    multiline?: boolean
    optionalLabels?: boolean
    showLangTitle?: boolean
}

interface ITranslationsInnerProps extends ITranslationsProps {
    languages: Language[]
    isTranslationOptional(isoCode: string): boolean
    isFirstOptionalLanguageAfterMandatoryLanguage(isoCode: string): boolean
}

const Translations: FC<ITranslationsInnerProps> = ({
    translations,
    onChange,
    validationErrors,
    direction = "vertical",
    languages,
    isTranslationOptional,
    isFirstOptionalLanguageAfterMandatoryLanguage,
    readOnly,
    multiline,
    optionalLabels,
    showLangTitle,
}) => {
    function handleChange(language: string, value: string) {
        onChange({
            ...translations,
            [language]: value,
        })
    }

    function getItemClassNames(language: Language) {
        const classNames: string[] = direction === "horizontal" ? ["py-1-5"] : []

        if (!isFirstOptionalLanguageAfterMandatoryLanguage(language.isoCode)) return classNames.join(" ")

        classNames.push(direction === "horizontal"
            ? "pl-3 border-left-grey-100"
            : "pt-3 border-top-grey-100"
        )

        return classNames.join(" ")
    }

    function renderInputAndValidationMessage(language: Language) {
        return (
            <>
                {readOnly
                    ? (translations[language.isoCode] || <NoValue />)
                    : <FormField optional={optionalLabels && isTranslationOptional(language.isoCode)}>
                        <Input
                            type={multiline ? "textarea" : "text"}
                            rows={multiline ? 20 : undefined}
                            maxLength={multiline ? INPUT_MAX_LENGTH_LARGE : INPUT_MAX_LENGTH_DEFAULT}
                            value={translations[language.isoCode] || ""}
                            onChange={e => handleChange(language.isoCode, e.target.value)}
                            isInvalid={!!validationErrors?.[language.isoCode]}
                        />
                    </FormField>
                }
                {!!validationErrors?.[language.isoCode] &&
                    <ValidationMessage>
                        {validationErrors[language.isoCode].message}
                    </ValidationMessage>
                }
            </>
        )
    }

    if (languages.length === 0) return null

    if (direction === "horizontal") {
        return (
            <Grid columns="1fr 2fr" gap="2.4rem">
                <GridItem key={languages[0].isoCode}
                    className={getItemClassNames(languages[0])}>
                    {showLangTitle && <Text className="pb-2">{languages[0].title}</Text>}
                    {renderInputAndValidationMessage(languages[0])}
                </GridItem>
                <Grid columns={`repeat(${languages.length - 1}, calc(50% - 2.4rem)`} gap="2.4rem" className="overflow-auto">
                    {languages.length > 0 && [...languages].splice(1).map(language => (
                        <GridItem key={language.isoCode}
                            className={getItemClassNames(language)}>
                            <Text className="pb-2">{language.title}</Text>
                            {renderInputAndValidationMessage(language)}
                        </GridItem>
                    ))}
                </Grid>
            </Grid>
        )
    }

    return (
        <Grid columns="1fr" gap="2.4rem">
            {languages.length > 0 && languages.map(language => (
                <Grid key={language.isoCode}
                    columns="1fr"
                    gap="2.4rem"
                    className={getItemClassNames(language)}>
                    <FormField
                        label={language.title}
                        optional={!readOnly && isTranslationOptional(language.isoCode)}
                        direction="horizontal"
                        isText={readOnly}>
                        {renderInputAndValidationMessage(language)}
                    </FormField>
                </Grid>
            ))}
        </Grid>
    )
}

export default Translations
